import { useCallback, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

/** DRF `next` URL → page number. No `next` means the list is complete. */
export function nextPageFromResponse(lastPage) {
  if (!lastPage || Array.isArray(lastPage) || !lastPage.next) return undefined;
  const raw = String(lastPage.next);
  try {
    return new URL(raw, 'http://local').searchParams.get('page') || undefined;
  } catch {
    const match = raw.match(/[?&]page=(\d+)/);
    return match ? match[1] : undefined;
  }
}

export function rowsFromPages(data, idOf) {
  const seen = new Set();
  const out = [];
  for (const page of data?.pages || []) {
    const list = Array.isArray(page) ? page : page?.results || [];
    for (const row of list) {
      const id = idOf(row);
      const key = id == null ? JSON.stringify(row) : String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
  }
  return out;
}

/** Server page when DRF `count`/`next` says so. Otherwise slice the full list. */
export function pageWindow(payload, page, pageSize) {
  const raw = Array.isArray(payload) ? payload : payload?.results || [];
  const reported = payload?.count;
  const serverPaged =
    payload &&
    !Array.isArray(payload) &&
    typeof reported === 'number' &&
    (reported > raw.length || payload.next != null || payload.previous != null);
  if (serverPaged) return { rows: raw, totalCount: reported };
  const start = (Math.max(1, page) - 1) * pageSize;
  return { rows: raw.slice(start, start + pageSize), totalCount: raw.length };
}

export function listParams(page, search) {
  const params = { page };
  const q = String(search || '').trim();
  if (q) params.search = q;
  return params;
}

export function usePagedList({ queryKey, enabled, queryFn, idOf }) {
  const [search, setSearch] = useState('');
  const query = useInfiniteQuery({
    queryKey: [...queryKey, search],
    queryFn: ({ pageParam }) => queryFn({ page: pageParam, search }),
    initialPageParam: 1,
    getNextPageParam: nextPageFromResponse,
    enabled,
  });

  const rows = rowsFromPages(query.data, idOf);
  const { hasNextPage, isFetchingNextPage, isFetching, fetchNextPage } = query;
  const loadingMore = isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    if (isFetching && !isFetchingNextPage) return;
    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetching, fetchNextPage]);

  return {
    rows,
    setSearch,
    hasMore: Boolean(hasNextPage),
    isFetchingMore: loadingMore,
    loadMore,
  };
}

export function useStickyRow(rows, value, idOf) {
  const remembered = useRef(null);
  const empty = value === '' || value == null;
  const match = empty
    ? null
    : rows.find((row) => String(idOf(row)) === String(value));
  if (empty) remembered.current = null;
  else if (match) remembered.current = match;
  else if (
    remembered.current &&
    String(idOf(remembered.current)) !== String(value)
  ) {
    remembered.current = null;
  }
  return match || remembered.current;
}
export function useStickyOptions(options, value) {
  const remembered = useRef(null);
  const empty = value === '' || value == null;
  const match = empty
    ? null
    : options.find((o) => String(o.value) === String(value));
  if (empty) remembered.current = null;
  else if (match) remembered.current = match;
  else if (
    remembered.current &&
    String(remembered.current.value) !== String(value)
  ) {
    remembered.current = null;
  }
  if (
    remembered.current &&
    !options.some((o) => String(o.value) === String(remembered.current.value))
  ) {
    return [remembered.current, ...options];
  }
  return options;
}
